import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const STATUS_COLORS = {
  new: "bg-emerald-50 text-emerald-700",
  contacted: "bg-blue-50 text-blue-700",
  qualified: "bg-purple-50 text-purple-700",
  archived: "bg-zinc-100 text-zinc-500",
};
const STATUSES = ["new", "contacted", "qualified", "archived"];

export default function LeadsPage() {
  const { adminAxios } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteInput, setNoteInput] = useState("");

  const load = () => adminAxios.get("/admin/leads").then(({ data }) => setLeads(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await adminAxios.put(`/admin/leads/${id}`, { status });
    load();
    if (selected?.id === id) setSelected(s => ({ ...s, status }));
  };

  const saveNote = async () => {
    if (!selected) return;
    await adminAxios.put(`/admin/leads/${selected.id}`, { notes: noteInput });
    load();
    setSelected(s => ({ ...s, notes: noteInput }));
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminAxios.delete(`/admin/leads/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const select = (lead) => { setSelected(lead); setNoteInput(lead.notes || ""); };

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Leads</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{leads.filter(l => l.status === "new").length} new, {leads.length} total</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                {["Name", "Phone", "Type", "Status", "Date", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-zinc-400">Loading...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-zinc-400 text-center">No leads yet.</td></tr>
              ) : leads.map(l => (
                <tr key={l.id} onClick={() => select(l)} className={`border-b border-zinc-50 cursor-pointer transition-colors ${selected?.id === l.id ? "bg-red-50" : "hover:bg-zinc-50"}`} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900">{l.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{l.phone}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs capitalize">{l.type || "lead"}</td>
                  <td className="px-4 py-3">
                    <select value={l.status} onChange={e => { e.stopPropagation(); updateStatus(l.id, e.target.value); }} onClick={e => e.stopPropagation()} className={`text-xs px-2 py-0.5 font-medium border-0 bg-transparent cursor-pointer focus:outline-none ${STATUS_COLORS[l.status] || ""}`} data-testid={`lead-status-${l.id}`}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><button onClick={e => { e.stopPropagation(); del(l.id); }} className="text-zinc-300 hover:text-[#C8102E] transition-colors"><Trash2 size={13} strokeWidth={1.5} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        <div className="bg-white border border-zinc-200 p-5">
          {selected ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Name</p>
                <p className="font-medium text-zinc-900">{selected.name}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Phone</p>
                <p className="text-zinc-700">{selected.phone}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Date</p>
                <p className="text-zinc-500 text-sm">{new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Status</p>
                <select value={selected.status} onChange={e => updateStatus(selected.id, e.target.value)} className={`text-xs px-3 py-1.5 border border-zinc-200 focus:outline-none bg-white`} data-testid="selected-lead-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Notes</p>
                <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-none" placeholder="Add notes..." data-testid="lead-notes-input" />
                <button onClick={saveNote} className="mt-2 text-xs bg-zinc-800 text-white px-4 py-1.5 hover:bg-zinc-700 transition-colors">Save Note</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-zinc-300 text-sm">Select a lead to view details</div>
          )}
        </div>
      </div>
    </div>
  );
}
